import * as yup from 'yup';
import { addMinutes } from '../../../library/dateTimeHelper';
import {
    ENTRY_MINUTES_BOUNDS,
    integerIn,
    MAX_ENTRY_DAYS,
    TranslateFn,
} from '../../../library/validationRules';
import ResourcesIds from '../../../resources/ResourcesIds';

/** What the add-to-my-day dialog holds while it is being filled in. */
export interface NewEntryFormValues {
    /** Time off rather than a booking: the tab, and the reason two fields go away. */
    isBlock: boolean;
    patientId: string;
    /** Tagged so one pick can name a plan or a catalogue line: 'plan:<id>' | 'service:<id>'. */
    choice: string;
    /**
     * Whose day this goes into. Seeded with whoever is filling the form in, and never empty
     * once the dialog is open -- a colleague's day is chosen, the caller's own is the default.
     *
     * It used to be the *handover* field, present only when the caller did not give the chosen
     * service and blank the rest of the time. That made the ordinary case -- booking a
     * colleague for something they both give -- impossible to say, and said nothing at all
     * about who the entry was for until it had already gone somewhere.
     */
    therapistId: string;
    /** Local wall clock, as `<input type="datetime-local">` reads it. */
    startsAt: string;
    /**
     * When a block ends, in the same wall-clock shape as `startsAt`. A block's own field: it
     * can run for days, which a count of minutes is no way to say. Unused on a booking, whose
     * length comes from what was sold or from `minutes`.
     */
    endsAt: string;
    /** A booking's length when neither a service nor a plan answers it. Unused on a block. */
    minutes: string;
}

/** `<date>T<HH:mm>`, the whole of it -- a day with no hour on it is not a moment. */
const COMPLETE_WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

/** The latest a block starting then may end, as a comparable `<date>T<HH:mm>`. */
export const latestEndFor = (startsAt: string): string =>
    addMinutes(startsAt, MAX_ENTRY_DAYS * 24 * 60).slice(0, 16);

/**
 * A booking needs somebody to be for; a block does not, which is the only difference
 * between the two tabs and so the only thing conditional here.
 *
 * A booking's typed length used to be unvalidated free text that fell back to sixty on
 * anything unreadable -- so "abc" silently booked an hour. It is now a whole number of
 * minutes, and ENTRY_MINUTES_BOUNDS is what the input's own spinner reads too.
 *
 * A block asks when it ends instead. It shared the minutes field and its eight-hour ceiling
 * until a week of leave had to be five blocks; it can now last up to MAX_ENTRY_DAYS, the
 * API's own bound. Both ends are compared as strings, which is exact because they share one
 * zero-padded shape.
 *
 * Deliberately *not* bounded by the clinic's opening hours: a therapist may book whenever
 * they need to, because an emergency at eight in the evening is a real thing. What bounds
 * `startsAt` is the API's own answer about which windows are free -- the whole day minus what
 * already occupies it -- which the dialog enforces by only offering those, not by a rule here.
 */
export const createNewEntrySchema = (translate: TranslateFn) => yup.object({
    isBlock: yup.boolean().defined(),

    patientId: yup.string().defined().when('isBlock', {
        is: false,
        then: (field) => field.test('required',
            translate(ResourcesIds.MY_CALENDAR_ADD_PATIENT_REQUIRED),
            (value) => !!value?.trim()),
    }),

    choice: yup.string().defined().when('isBlock', {
        is: false,
        then: (field) => field.test('required',
            translate(ResourcesIds.MY_CALENDAR_ADD_SERVICE_REQUIRED),
            (value) => !!value?.trim()),
    }),

    // Always required, on both tabs. There is no unspoken default any more: an entry goes
    // into a named diary, and which one decides what the hour list is even allowed to offer.
    therapistId: yup.string()
        .test('required', translate(ResourcesIds.MY_CALENDAR_ADD_THERAPIST_REQUIRED),
            (value) => !!value?.trim())
        .defined(),

    // A day with no hour on it is not a start. The picker emits '' when either half is
    // missing, but the dialog also writes a bare '<date>T' of its own -- when the time it was
    // holding stops being free, the day is kept and the hour is asked again -- and a plain
    // `required` reads that as answered, since it is a non-empty string.
    startsAt: yup.string()
        .required(translate(ResourcesIds.MY_CALENDAR_ADD_STARTS_AT_REQUIRED))
        .test('complete', translate(ResourcesIds.MY_CALENDAR_ADD_STARTS_AT_REQUIRED),
            (value) => /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value ?? '')),

    // A block's end: required, after its start, and no further than the API allows.
    endsAt: yup.string().defined().when('isBlock', {
        is: true,
        then: (field) => field
            .test('required', translate(ResourcesIds.MY_CALENDAR_ADD_UNTIL_REQUIRED),
                (value) => COMPLETE_WALL_CLOCK.test(value ?? ''))
            .test('after-start', translate(ResourcesIds.MY_CALENDAR_ADD_UNTIL_AFTER_START),
                function (value) {
                    const { startsAt } = this.parent as NewEntryFormValues;
                    // Nothing to compare with until the start is whole; its own rule speaks.
                    return !COMPLETE_WALL_CLOCK.test(startsAt ?? '')
                        || (value ?? '').slice(0, 16) > startsAt.slice(0, 16);
                })
            .test('too-long',
                translate(ResourcesIds.MY_CALENDAR_ADD_UNTIL_TOO_LONG, { days: MAX_ENTRY_DAYS }),
                function (value) {
                    const { startsAt } = this.parent as NewEntryFormValues;
                    return !COMPLETE_WALL_CLOCK.test(startsAt ?? '')
                        || (value ?? '').slice(0, 16) <= latestEndFor(startsAt);
                }),
    }),

    // A booking's own length, optional because a service or a plan usually answers it.
    // Nothing on a block, which says when it ends instead.
    minutes: yup.string().defined().when('isBlock', {
        is: false,
        then: () => integerIn(ENTRY_MINUTES_BOUNDS, translate,
            ResourcesIds.MY_CALENDAR_ADD_MINUTES_INVALID),
    }),
});
